import { useEffect, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { CURRENCIES, currencyMeta, applyTenantCurrency } from "@/lib/currency";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const KEY = "kairosa_landing_currency";
const EVT = "kairosa:landing-currency-change";

/**
 * Reads the visitor's preferred currency for the public landing page from
 * localStorage (falls back to browser locale guess, then USD). Persists to
 * both localStorage AND `document.documentElement.dataset.currency` so any
 * `formatMoney(x)` call on the landing page picks it up automatically.
 */
export function readLandingCurrency() {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored) return stored.toUpperCase();
  } catch {}
  // Best-effort guess from browser locale
  try {
    const guess = new Intl.NumberFormat(navigator.language, { style: "currency", currency: "USD" })
      .resolvedOptions().currency;
    if (guess) return guess;
  } catch {}
  return "USD";
}

/** Small hook — subscribes to switcher changes so components re-render. */
export function useLandingCurrency() {
  const [code, setCode] = useState(() => readLandingCurrency());
  useEffect(() => {
    applyTenantCurrency(code);
    const handler = (e) => setCode(e.detail);
    window.addEventListener(EVT, handler);
    return () => window.removeEventListener(EVT, handler);
  }, [code]);
  return code;
}

/**
 * Currency chip for the top nav of the public landing page (no auth).
 * Persists to localStorage, updates the doc-level dataset so all pricing
 * on the page rerenders in the new currency.
 */
export function LandingCurrencySwitcher() {
  const [current, setCurrent] = useState(() => currencyMeta(readLandingCurrency()));
  const [open, setOpen] = useState(false);

  useEffect(() => { applyTenantCurrency(current.code); }, [current]);

  const pick = (code) => {
    try { localStorage.setItem(KEY, code); } catch {}
    applyTenantCurrency(code);
    window.dispatchEvent(new CustomEvent(EVT, { detail: code }));
    setCurrent(currencyMeta(code));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="landing-currency-btn"
          title={`Show pricing in ${current.label} (${current.code})`}
          aria-label={`Currency: ${current.label}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <span className="text-base leading-none">{current.flag}</span>
          <span className="font-mono uppercase tracking-wider text-[11px]">{current.code}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0 max-h-80 overflow-y-auto" data-testid="landing-currency-popover">
        <div className="p-1">
          {CURRENCIES.map((c) => (
            <button
              key={c.code}
              onClick={() => pick(c.code)}
              data-testid={`landing-currency-item-${c.code}`}
              className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors text-left"
            >
              <span className="text-base leading-none">{c.flag}</span>
              <span className="font-mono w-10 text-[11px] uppercase text-muted-foreground">{c.code}</span>
              <span className="flex-1 truncate">{c.label}</span>
              <span className="text-muted-foreground text-xs">{c.symbol}</span>
              {c.code === current.code && <Check className="h-3.5 w-3.5 text-primary" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
